import { useQuery } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';

export interface NotifItem {
  id: string;
  label: string;
  sublabel?: string;
  link: string;
  at?: string;
}

export interface NotifCategory {
  key: string;
  label: string;
  count: number;
  /** count hit the server's per-queue cap — render "50+" */
  overflow: boolean;
  link: string;
  items: NotifItem[];
}

export interface AdminNotifications {
  total: number;
  categories: NotifCategory[];
}

/**
 * The header bell's feed — every admin queue that needs a human action
 * (applications, approved-but-unfinished setups, customers with no billing
 * address, names that disagree with QuickBooks, change requests, EDI review,
 * audits, returned payments, held payouts). Superadmin-only; polls every 60s
 * so new work badges the bell without a refresh.
 *
 * Every category is DERIVED from live state — there is no dismissal to go
 * stale, so an item leaves the bell the moment the thing is actually fixed.
 */
export function useAdminNotifications() {
  const { isSuperadmin } = useIsSuperadmin();
  return useQuery({
    queryKey: ['admin-notifications'],
    queryFn: () => adminFetch<AdminNotifications>('admin-notifications'),
    enabled: isSuperadmin,
    refetchInterval: 60_000,
    staleTime: 30_000,
    // The bell must never error-toast the whole app — stale data is fine.
    retry: 1,
  });
}
